import { useEffect, useState } from 'react'
import { toast } from 'sonner'

export type BrowserNotificationPermission = NotificationPermission | 'unsupported'

function readPermission(): BrowserNotificationPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
  return Notification.permission
}

/**
 * Tracks `Notification.permission` for the current origin.
 * Denied cannot be fixed by the app — user must Allow in site settings.
 */
export function useBrowserNotificationPermission(): BrowserNotificationPermission {
  const [permission, setPermission] = useState<BrowserNotificationPermission>(readPermission)

  useEffect(() => {
    const sync = () => {
      setPermission(readPermission())
    }

    let status: PermissionStatus | null = null
    const onChange = () => sync()

    if (typeof navigator !== 'undefined' && navigator.permissions?.query) {
      void navigator.permissions
        .query({ name: 'notifications' as PermissionName })
        .then((s) => {
          status = s
          status.addEventListener('change', onChange)
          sync()
        })
        .catch(() => {})
    }

    window.addEventListener('focus', sync)
    document.addEventListener('visibilitychange', sync)

    return () => {
      status?.removeEventListener('change', onChange)
      window.removeEventListener('focus', sync)
      document.removeEventListener('visibilitychange', sync)
    }
  }, [])

  return permission
}

const TOAST_ID = 'pmms-notification-permission-denied'
/** Closed once → stays quiet until permission changes. */
const DISMISSED_KEY = 'pmms.notifications.deniedToastDismissed'

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

function writeDismissed(value: boolean): void {
  try {
    if (value) localStorage.setItem(DISMISSED_KEY, '1')
    else localStorage.removeItem(DISMISSED_KEY)
  } catch {
    // blocked storage — the toast just shows again next visit
  }
}

/** One toast when this origin has blocked notifications — until the user closes it. */
export function useDeniedNotificationToast(enabled: boolean): void {
  const permission = useBrowserNotificationPermission()

  useEffect(() => {
    if (permission === 'granted') writeDismissed(false)
    if (!enabled || permission !== 'denied' || readDismissed()) return

    toast.warning('Notifications blocked. Allow them in site settings.', {
      id: TOAST_ID,
      duration: 12_000,
      onDismiss: () => writeDismissed(true),
    })
  }, [enabled, permission])
}
